import React, { memo } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Field, getIn } from 'formik';
import { Input as SemanticInput, TextArea } from 'semantic-ui-react';
import MaskedInput from 'react-text-mask';
import classNames from 'classnames';
import './Input.css';
import { Masks } from '../../../shared/helpers';
import RedCross from '../../Login/red-cross.svg';
import BlueTick from '../../Login/blue-tick.svg';
import PhoneNumberInput from './PhoneNumberInput';

const textAreaErrorStyle = {
  background: '#fff6f6',
  borderColor: '#e0b4b4',
  color: '#9f3a38',
  borderRadius: '',
  boxShadow: 'none',
};

const isDefaultType = type => type !== 'textArea' && type !== 'phoneNumber';

const checkMask = (mask, type) => mask || Masks[type];

const Input = ({
  name,
  label,
  type,
  mask,
  containerClassName,
  inputContainerClassName,
  labelClassName,
  inputClassName,
  invalid,
  showError,
  fluid,
  ddi,
  placeholder,
  oldStyle,
  hasInput,
  ...rest
}) => (
  <Field
    type={type}
    name={name}
    render={({ field, form }) => {
      const error = getIn(form.errors, field.name);
      const touched = getIn(form.touched, field.name);
      const showFieldError = showError && touched && !!error;
      return oldStyle ? (
        <div
          className={classNames(
            oldStyle && 'col-12 col-md mb-3',
            containerClassName,
          )}
        >
          {!isEmpty(label) && (
            <label
              className={classNames(
                labelClassName,
                showFieldError && 'text-danger',
              )}
            >
              {label}
            </label>
          )}
          <div
            className={classNames(
              'input__container w-100',
              inputContainerClassName,
            )}
          >
            {type === 'textArea' && (
              <TextArea
                className={classNames('form-control', inputClassName)}
                autoHeight
                {...field}
                style={showFieldError ? textAreaErrorStyle : null}
              />
            )}
            {isDefaultType(type) && (
              <SemanticInput
                className="w-100"
                error={showFieldError}
                fluid={fluid}
              >
                <MaskedInput
                  {...field}
                  {...rest}
                  className={classNames(
                    'form-control',
                    inputClassName,
                    showFieldError && 'is-invalid',
                  )}
                  mask={checkMask(mask, type)}
                  guide={false}
                  type={type}
                />
              </SemanticInput>
            )}
            {invalid !== undefined &&
              (invalid ? (
                <img
                  alt="input invalid"
                  src={RedCross}
                  className="input__icon"
                />
              ) : (
                <img alt="input valid" src={BlueTick} className="input__icon" />
              ))}
            {showFieldError && (
              <div className="invalid-feedback d-block">{error}</div>
            )}
          </div>
        </div>
      ) : (
        <div
          className={classNames(
            'input__container w-100',
            inputContainerClassName,
          )}
        >
          <label
            className={`input ${hasInput ? 'has-input' : ''} ${
              type === 'textArea' ? 'textarea' : ''
            }`}
          >
            <span className="input-label text-gray-light">{label}</span>
            {isDefaultType(type) && (
              <SemanticInput
                className="w-100"
                error={showFieldError}
                fluid={fluid}
              >
                <MaskedInput
                  {...field}
                  {...rest}
                  className={classNames(
                    'input-field',
                    showFieldError && 'is-invalid',
                  )}
                  mask={checkMask(mask, type)}
                  guide={false}
                  type={type}
                  placeholder={label}
                />
              </SemanticInput>
            )}
            {type === 'textArea' && (
              <TextArea
                className={classNames('input-field')}
                autoHeight
                {...field}
                onBlur={rest.onChange}
                onChange={e => {
                  form.setFieldValue(field.name, e.target.value);
                }}
                style={showFieldError ? textAreaErrorStyle : null}
                placeholder= {`${label} (optional)\n${true ? 'Please provide any additional project specifics or link your brief on the following page.' : null } `}
              />
            )}
            {showFieldError && (
              <div className="invalid-feedback d-block">{error}</div>
            )}
          </label>
        </div>
      );
    }}
  />
);

Input.defaultProps = {
  type: 'text',
  mask: false,
  showError: true,
  oldStyle: true,
  hasInput: false,
};

Input.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.string,
  mask: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.any), PropTypes.bool]),
  containerClassName: PropTypes.string,
  inputContainerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  ddi: PropTypes.string,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
  showError: PropTypes.bool,
  minHeight: PropTypes.number,
  fluid: PropTypes.bool,
  oldStyle: PropTypes.bool,
  hasInput: PropTypes.bool,
};

const memoInput = memo(Input);
export { memoInput as Input };



// WEBPACK FOOTER //
// ./src/components/Form/Input/Input.js